import React from 'react';
import teacherService from '../../services/api/teacher';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { getArabicDate } from '../../services/utils/date';
import { useSettings } from '../../contexts/SettingsContext';
import { 
  BookOpen, 
  GraduationCap, 
  ArrowRight, 
  ShieldCheck, 
  Layers, 
  Info, 
  ChevronLeft, 
  CheckCircle2,
  Clock
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const Subjects = () => {
  const navigate = useNavigate();
  const { getWhatsAppLink } = useSettings();
  
  // جلب بيانات المواد والصفوف الخاصة بالمدرس (مع تمييز الـ query key)
  const { data: subjects, isLoading, error } = useQuery(['teacher-subjects', 'list'], () => teacherService.getSubjects(), {
    retry: false,
    staleTime: 300000,
    refetchOnWindowFocus: false
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري تحميل تخصصاتك الدراسية...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <Info className="w-8 h-8 text-red-500" />
        </div>
        <h3 className="text-xl font-black text-gray-900 mb-2">فشل في تحميل البيانات</h3>
        <p className="text-gray-500 max-w-sm mx-auto mb-6">حدث خطأ أثناء جلب تخصصاتك الدراسية. يرجى المحاولة مرة أخرى.</p>
        <button 
          onClick={() => window.location.reload()}
          className="bg-primary-600 text-white px-6 py-2 rounded-xl font-black hover:bg-primary-700 transition-all"
        >
          إعادة المحاولة
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-10 px-4 sm:px-6 py-8 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in slide-in-from-top duration-500">
        <div>
          <button 
            onClick={() => navigate('/dashboard')}
            className="group flex items-center text-gray-400 hover:text-primary-600 font-bold transition-all mb-2 text-sm"
          >
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            العودة للوحة التحكم
          </button>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <GraduationCap className="bg-primary-600 text-white p-1.5 rounded-xl" size={32} />
            تخصصاتي التعليمية
          </h1>
        </div>
        
        <div className="bg-white px-5 py-2.5 rounded-2xl border border-gray-100 shadow-sm text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
            <ShieldCheck size={14} className="text-emerald-500" />
            تاريخ اليوم: {getArabicDate(new Date())}
        </div>
      </div>

      {/* Summary Dashboard Card */}
      <div className="bg-primary-600 rounded-[2.5rem] p-8 lg:p-10 text-white relative overflow-hidden shadow-2xl shadow-primary-200 group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -z-0 translate-x-1/3 -translate-y-1/3 group-hover:scale-110 transition-transform duration-700"></div>
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="space-y-3 text-center md:text-right">
                <h2 className="text-2xl lg:text-3xl font-black leading-tight">المواد والصفوف المعتمدة</h2>
                <p className="text-primary-100 font-medium max-w-md opacity-90">
                    هذه القائمة تحتوي على المواد والصفوف التي تمت الموافقة عليها من قبل الإدارة، والتي يمكنك إنشاء كورسات تعليمية فيها.
                </p>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-3xl border border-white/20 text-center min-w-[180px]">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-1 opacity-70">إجمالي التخصصات</p>
                <p className="text-4xl font-black">{subjects?.length || 0}</p>
            </div>
        </div>
      </div>

      {/* Main Subjects Content Area */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-12 relative overflow-hidden group">
        <div className="absolute top-0 left-0 w-2 h-full bg-primary-600 opacity-20 group-hover:opacity-100 transition-opacity"></div>
        
        <div className="flex items-center justify-between mb-10">
            <h3 className="text-xl font-black text-gray-900 flex items-center gap-3">
                <Layers size={20} className="text-primary-600" /> قائمة التخصصات الحالية
            </h3>
        </div>
        
        {subjects && subjects.length > 0 ? (
          <div className="grid grid-cols-1 gap-4">
            {subjects.map((item) => (
              <div 
                key={item.teacher_subject_id} 
                className="group/item flex flex-col md:flex-row items-center justify-between p-6 border border-gray-50 bg-gray-50/50 rounded-[2rem] hover:bg-white hover:shadow-xl hover:border-primary-100 transition-all duration-300 gap-6"
              >
                <div className="flex items-center gap-6 w-full md:w-auto text-center md:text-right">
                  <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-primary-600 shadow-sm group-hover/item:scale-110 group-hover/item:bg-primary-600 group-hover/item:text-white transition-all duration-500">
                    <BookOpen size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-gray-900 group-hover/item:text-primary-600 transition-colors">
                        {item.subject_name}
                    </h3>
                    <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-2">
                        <span className="bg-primary-50 text-primary-600 px-3 py-1 rounded-lg text-[10px] font-black border border-primary-100 uppercase tracking-widest">
                            {item.grade_name}
                        </span>
                        <span className="bg-gray-100 text-gray-500 px-3 py-1 rounded-lg text-[10px] font-black border border-gray-200 uppercase tracking-widest">
                            {item.stage_name}
                        </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                    <div className="hidden lg:flex items-center gap-2 text-emerald-600 bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-100 text-[10px] font-black uppercase tracking-widest">
                        <CheckCircle2 size={14} /> تخصص مفعل
                    </div>
                    <button 
                        onClick={() => navigate(`/teacher/courses?subject=${item.teacher_subject_id}`)}
                        className="w-12 h-12 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-all shadow-sm"
                        title="عرض الكورسات التابعة"
                    >
                        <ChevronLeft size={20} />
                    </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 animate-in fade-in duration-700">
            <div className="bg-gray-50 w-24 h-24 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-inner border border-gray-100">
              <BookOpen className="w-12 h-12 text-gray-300" />
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">لا توجد مواد معتمدة حتى الآن</h3>
            <p className="text-gray-400 max-w-sm mx-auto font-medium leading-relaxed">
              بمجرد موافقة مدير النظام على تخصصاتك المختارة، ستظهر هنا تلقائياً لتتمكن من إنشاء أول كورس لك.
            </p>
            <button 
                onClick={() => navigate('/dashboard')}
                className="mt-8 bg-primary-600 text-white px-10 py-3 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center justify-center gap-2 mx-auto"
            >
                الانتظار في لوحة التحكم <ChevronLeft size={18} />
            </button>
          </div>
        )}
      </div>

      {/* Important Information Box */}
      <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white relative overflow-hidden group shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/20 rounded-full blur-[100px] -z-0"></div>
        <div className="relative z-10 space-y-6">
            <div className="flex items-center gap-3 mb-2">
                <div className="bg-primary-600 w-12 h-12 rounded-2xl flex items-center justify-center shadow-xl">
                    <Info size={24} />
                </div>
                <h3 className="text-2xl font-black tracking-tight leading-tight">إرشادات تخصص المدرس</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                    'هذه المواد تمثل نطاق عملك الأكاديمي الذي تم تحديده أثناء مرحلة التسجيل.',
                    'لا يمكنك إنشاء كورسات تعليمية خارج نطاق هذه المواد والصفوف المعتمدة لك.',
                    'في حال رغبت في إضافة مادة جديدة أو تغيير صف دراسي، يرجى مراسلة الإدارة.'
                ].map((text, i) => (
                    <div key={i} className="bg-white/5 border border-white/10 p-5 rounded-3xl hover:bg-white/10 transition-all group/info">
                        <div className="flex items-start gap-4">
                            <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0 group-hover/info:scale-150 transition-transform"></div>
                            <p className="text-sm font-medium text-gray-400 leading-relaxed group-hover/info:text-white transition-colors">{text}</p>
                        </div>
                    </div>
                ))}
            </div>

            <div className="pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest flex items-center gap-2">
                    <Clock size={14} /> وقت معالجة الطلبات: 24-48 ساعة
                </p>
                <button
                    onClick={() => {
                      const message = encodeURIComponent('مرحباً، أحتاج مساعدة فنية بخصوص حسابي كمُدرس على المنصة.');
                      try {
                        const url = getWhatsAppLink ? getWhatsAppLink(message) : null;
                        if (url) {
                          window.open(url, '_blank');
                        } else {
                          window.location.href = 'mailto:support@almanassa.com';
                        }
                      } catch (err) {
                        window.location.href = 'mailto:support@almanassa.com';
                      }
                    }}
                    className="text-primary-400 font-black text-sm uppercase tracking-widest flex items-center gap-1 hover:gap-3 transition-all"
                >
                    تواصل مع الدعم الفني <ChevronLeft size={16} />
                </button>
            </div>
        </div>
      </div>
    </div>
  );
};

export default Subjects;